import { ArSlideJson } from "@custom-types/core-api-legacy-types";
import { IElementMarkupWithTypeHint } from "@custom-types/i-elements-types";
import { Markup } from "@custom-types/project-markups-types";
import { isMarkupSceneObject } from "@custom-types/slide-nodes-type-guards";
import { MarkupSceneObject } from "@custom-types/slide-nodes-types";
import {
  AnnotationStatus,
  IElementMeasurePolygon,
  IElementModel3D,
} from "@faro-lotv/ielement-types";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import { DateTimeUtils } from "@stellar/utils";
import { Matrix4Tuple } from "three";
import { getMeasurements } from "@utils/vector-utils";
import { convertArea, convertDistance } from "@utils/unit-utils";

/**
 * @returns An array of Markups sorted like this:
 * - Markups with most recently modified should show first
 *
 * @param markups Array of Markup entities.
 */
export function sortMarkups(markups: Markup[]): Markup[] {
  return markups.sort((a, b) => {
    const aModifiedAt = new Date(a.modifiedAt).getTime();
    const bModifiedAt = new Date(b.modifiedAt).getTime();

    return bModifiedAt - aModifiedAt;
  });
}

interface SlideNodeAndSceneObject {
  /** Slide node associated to the markup */
  slideNode: ArSlideJson;

  /** Scene object associated to the markup */
  sceneObject: MarkupSceneObject;
}

/** Finds the slideNode and scene object associated to the markup */
export function findSlideNodeAndSceneObject(
  slideNodes: ArSlideJson[],
  markup: IElementMarkupWithTypeHint,
  model3d?: IElementModel3D
): SlideNodeAndSceneObject | undefined {
  for (const slideNode of slideNodes) {
    const sceneObject = findSceneObject(slideNode, markup, model3d);
    if (sceneObject) {
      return { slideNode, sceneObject };
    }

    const nestedSlideNodes = slideNode.slideNodes;
    if (nestedSlideNodes) {
      const result = findSlideNodeAndSceneObject(
        nestedSlideNodes,
        markup,
        model3d
      );
      if (result) {
        return result;
      }
    }
  }
}

/** Finds the scene object associated to the markup */
function findSceneObject(
  slideNode: ArSlideJson,
  markup: IElementMarkupWithTypeHint,
  model3d?: IElementModel3D
): MarkupSceneObject | undefined {
  let foundSceneObject: MarkupSceneObject | undefined = undefined;

  const sceneObjects = slideNode.nodes;
  const markupSceneObjects = sceneObjects.filter(isMarkupSceneObject);

  // 1st attempt to find scene object by the md5Hash property
  foundSceneObject = findSceneObjectByMd5Hash(markupSceneObjects, model3d);
  if (foundSceneObject) {
    return foundSceneObject;
  }

  // As last resort attempt to find scene object by name + description
  foundSceneObject = findSceneObjectByNameAndDescription(
    markupSceneObjects,
    markup
  );

  return foundSceneObject;
}

/** Finds an scene object by the "md5Hash" property */
function findSceneObjectByMd5Hash(
  markupSceneObjects: MarkupSceneObject[],
  model3d?: IElementModel3D
): MarkupSceneObject | undefined {
  // Get the "md5Hash" property associated to the markup. This property, if available, has the same value
  // as the "md5hash" property of the scene object associated to the project.
  const md5Hash = model3d?.md5Hash;

  // Early exit if "md5Hash" value is not available
  if (!md5Hash) {
    return;
  }

  return markupSceneObjects.find(
    (sceneObject) => sceneObject.md5hash === md5Hash
  );
}

/** Finds an scene object by the name and description properties */
function findSceneObjectByNameAndDescription(
  markupSceneObjects: MarkupSceneObject[],
  markup: IElementMarkupWithTypeHint
): MarkupSceneObject | undefined {
  const name = markup.name.trim();
  const description = (markup.descr ?? "").trim();
  const nameAndDescription = `${name}${description}`;

  return markupSceneObjects.find((sceneObject) => {
    const sceneObjectName = sceneObject.displayName.trim();
    const sceneObjectDescription = (
      sceneObject.markupData.description ?? ""
    ).trim();
    const sceneObjectNameAndDescription = `${sceneObjectName}${sceneObjectDescription}`;
    return nameAndDescription === sceneObjectNameAndDescription;
  });
}

/** Returns true iff the markup status value is "To Do" */
export function isMarkupToDo(markup: Markup): boolean {
  return markup.status?.value === AnnotationStatus.Open;
}

/** Returns true iff the markup status value is "In Progress" */
export function isMarkupInProgress(markup: Markup): boolean {
  return markup.status?.value === AnnotationStatus.InProgress;
}

/** Returns true iff the markup status value is "In Review" */
export function isMarkupInReview(markup: Markup): boolean {
  return markup.status?.value === AnnotationStatus.ToReview;
}

/** Returns true iff the markup status value is "Resolved" */
export function isMarkupResolved(markup: Markup): boolean {
  return markup.status?.value === AnnotationStatus.Resolved;
}

/** Returns true iff the markup status value is not defined */
export function isMarkupUnclassified(markup: Markup): boolean {
  return !markup.status?.value;
}

/** Returns true iff the markup due date value is defined and is overdue */
export function isMarkupOverdue(markup: Markup): boolean {
  const dueDate = markup.dueDate?.value;

  // Markups without due date or with "Resolved" status can't be overdue
  if (!dueDate || isMarkupResolved(markup)) {
    return false;
  }

  return DateTimeUtils.isDateOverdue(dueDate);
}

interface GetUniqueMarkupPropertiesProps<V extends keyof Markup> {
  /** Array of Markups */
  markups: Markup[];

  /** Property name to extract the unique values */
  uniqueIdKey: V;
}

export function getUniqueMarkupProperties<V extends keyof Markup>({
  markups,
  uniqueIdKey,
}: GetUniqueMarkupPropertiesProps<V>): NonNullable<Markup[V]>[] {
  const allProps = markups
    .flatMap((markup) => markup[uniqueIdKey])
    .filter((prop): prop is Markup[V] => {
      return prop !== null && prop !== undefined;
    });

  const uniqueIds = [
    ...new Set(
      allProps.map((prop) => {
        if (prop && typeof prop === "object" && "id" in prop) {
          return prop.id;
        }
        return undefined;
      })
    ),
  ];

  // All the scenes that are unique
  return uniqueIds.reduce<NonNullable<Markup[V]>[]>((uniqueProps, uniqueId) => {
    const foundProp = allProps.find((prop) => {
      if (prop && typeof prop === "object" && "id" in prop) {
        return prop.id === uniqueId;
      }
      return false;
    });

    if (foundProp) {
      uniqueProps.push(foundProp);
    }
    return uniqueProps;
  }, []);
}

/** Props for the `getMarkupMeasurements` function */
interface GetMarkupMeasurementValuesProps {
  /** Measure polygon element associated to the markup/annotation */
  measurePolygon: IElementMeasurePolygon;

  /** World 4x4 matrix transform tuple of the measure polygon element */
  worldMatrix4Tuple: Matrix4Tuple;

  /** Distance unit to use */
  unitMeasurement: SettingsApiTypes.UnitMeasurement;
}

/** Return object of the `getMarkupMeasurements` function */
interface GetMarkupMeasurementValues {
  /** Whether the measure polygon is a closed loop */
  isClosed: boolean;

  /** Total length of the measure polygon */
  length: string;

  /** Total area of the measure polygon, only available if the polygon is closed */
  area?: string;

  /** Horizontal distance of the measure polygon, only available if it's a 2-point measurement */
  horizontal?: string;

  /** Vertical distance of the measure polygon, only available if it's a 2-point measurement */
  vertical?: string;

  /** Distance along the X axis, only available if it's a 2-point measurement */
  x?: string;

  /** Distance along the Y axis, only available if it's a 2-point measurement */
  y?: string;

  /** Distance along the Z axis, only available if it's a 2-point measurement */
  z?: string;
}

/**
 * Gets the measurement values of a measure polygon in the specified distance and area units
 * @returns An object with the measurement values as readable strings
 */
export function getMarkupMeasurements({
  measurePolygon,
  worldMatrix4Tuple,
  unitMeasurement,
}: GetMarkupMeasurementValuesProps): GetMarkupMeasurementValues {
  // Get the raw measurement values in the default units (meters for distance, square meters for area)
  const { length, area, horizontal, vertical, x, y, z } = getMeasurements({
    localPoints: measurePolygon.points,
    isClosed: measurePolygon.isClosed,
    worldMatrix4Tuple,
  });

  // Convert values to the specified unit
  const convertedLength = convertDistance(length, unitMeasurement);
  const convertedArea =
    typeof area === "number" ? convertArea(area, unitMeasurement) : undefined;
  const convertedHorizontal =
    typeof horizontal === "number"
      ? convertDistance(horizontal, unitMeasurement)
      : undefined;
  const convertedVertical =
    typeof vertical === "number"
      ? convertDistance(vertical, unitMeasurement)
      : undefined;
  const convertedX =
    typeof x === "number" ? convertDistance(x, unitMeasurement) : undefined;
  const convertedY =
    typeof y === "number" ? convertDistance(y, unitMeasurement) : undefined;
  const convertedZ =
    typeof z === "number" ? convertDistance(z, unitMeasurement) : undefined;

  // If the measurement is 2D pano image (e.g. taken from a 360 image) then the measurement
  // is indicated as an estimation. For this case we append a "~" symbol before the value.
  const isEstimatedFrom2DPano =
    !!measurePolygon.metaDataMap?.isEstimatedFrom2DPano;

  const formattedLength = isEstimatedFrom2DPano
    ? `~${convertedLength}`
    : convertedLength;
  const formattedArea =
    isEstimatedFrom2DPano && typeof convertedArea === "string"
      ? `~${convertedArea}`
      : convertedArea;
  const formattedHorizontal =
    isEstimatedFrom2DPano && typeof convertedHorizontal === "string"
      ? `~${convertedHorizontal}`
      : convertedHorizontal;
  const formattedVertical =
    isEstimatedFrom2DPano && typeof convertedVertical === "string"
      ? `~${convertedVertical}`
      : convertedVertical;
  const formattedX =
    isEstimatedFrom2DPano && typeof convertedX === "string"
      ? `~${convertedX}`
      : convertedX;
  const formattedY =
    isEstimatedFrom2DPano && typeof convertedY === "string"
      ? `~${convertedY}`
      : convertedY;
  const formattedZ =
    isEstimatedFrom2DPano && typeof convertedZ === "string"
      ? `~${convertedZ}`
      : convertedZ;

  return {
    isClosed: measurePolygon.isClosed,
    length: formattedLength,
    area: formattedArea,
    horizontal: formattedHorizontal,
    vertical: formattedVertical,
    x: formattedX,
    y: formattedY,
    z: formattedZ,
  };
}
